import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DateTime } from 'luxon';
import { differenceInDays, formatDistanceToNowStrict } from 'date-fns';
import { useCallMeBack } from '../../../hooks/useCallMeBack';
import { InfoBlockForChangeNow } from '../InfoBlock/InfoBlockForChangeNow';
import InfoBlockPrepaidOrDaily from '../InfoBlock/InfoBlockPrepaid/InfoBlockPrepaidOrDaily';
import { PreparedCalculateData } from '../../../models/Calculate';
import { useAppSelector } from '../../../hooks/redux';
import { useDateFsnLocale } from '../../../hooks/useDateFsnLocale';
import { useDateLocalization } from '../../../hooks/useDateLocalization';
import { ChangeCurrentServiceInfoType } from '../../../models/ServicePresets';
import { dateForChanging } from '../../../constants/configuration';
import { InfoBlockType } from '../../../pages/Payment/PaymentRefactor';

interface IRefactorInfoBlockProps {
  servicePrice: number | undefined;
  isMonthCountChanged: boolean;
  handleChangeCountMonth: (amount: number) => void;
  type: InfoBlockType;
  calculation: PreparedCalculateData | null;
  ChangeCurrentServiceInfo: ChangeCurrentServiceInfoType | undefined;
  isLoading: boolean;
  isShare: boolean;
}

export const RefactorInfoBlock: FC<IRefactorInfoBlockProps> = ({
  isMonthCountChanged,
  handleChangeCountMonth,
  type,
  servicePrice,
  ChangeCurrentServiceInfo,
  calculation,
  isLoading,
  isShare,
}) => {
  const isFirstRender = useRef(true);
  const { t } = useTranslation();
  const { callMeBack } = useCallMeBack();
  const [isLoaded, setIsLoaded] = useState(false);
  const { selectedLanguage } = useAppSelector((state) => state.langReducer);
  const { fnsLocale } = useDateFsnLocale(selectedLanguage);
  const { formatDate } = useDateLocalization();
  const availableInstantChanges = ChangeCurrentServiceInfo
    ? ChangeCurrentServiceInfo.max_count - ChangeCurrentServiceInfo.current_count
    : undefined;

  const dayDifference =
    differenceInDays(
      DateTime.fromSeconds(calculation?.view.tts || 0).toJSDate(),
      DateTime.fromSeconds(calculation?.view.fts || 0).toJSDate(),
    ) + 1;

  const daysRemain = calculation?.view.tts
    ? formatDistanceToNowStrict(DateTime.fromSeconds(calculation?.view.tts).setZone('Europe/Kiev').toJSDate(), {
        unit: 'day',
        locale: fnsLocale,
        roundingMethod: 'ceil',
      })
    : '';

  const untilDate = calculation?.view.tts
    ? formatDate(DateTime.fromSeconds(calculation.view.tts).setZone('Europe/Kiev').toJSDate(), {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        locale: 'uk',
      })
    : '';

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else if (!isLoading) {
      setIsLoaded(true);
    }
  }, [isLoading]);

  if (!isLoaded) return null;

  return (
    <>
      {type === 'now' && (
        <InfoBlockForChangeNow
          untilDate={untilDate}
          daysRemain={daysRemain}
          price={servicePrice}
          payAmount={calculation?.pay_amount}
          moreThanHalfMonth={isShare || dayDifference > +dateForChanging}
          isMonthCountChanged={isMonthCountChanged}
          handleChangeCountMonth={handleChangeCountMonth}
          availableInstantChanges={availableInstantChanges}
        />
      )}
      {type === 'hasPrepaid' && (
        <InfoBlockPrepaidOrDaily
          modifier="arrow"
          handleCallback={callMeBack}
          infoText={t('servicePayment.infoBlockPrepaid')}
        />
      )}
      {type === 'daily' && (
        <InfoBlockPrepaidOrDaily handleCallback={callMeBack} infoText={t('servicePayment.infoBlockDaily')} />
      )}
      {type === 'notPrepaid' && (
        <InfoBlockPrepaidOrDaily
          modifier="arrow"
          handleCallback={callMeBack}
          infoText={t('servicePayment.infoBlockOther')}
        />
      )}
    </>
  );
};
